/* ---------------------------------- */
/* IMPORTS */
/* ---------------------------------- */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import * as faceapi from "face-api.js";
import axios from "axios";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";

import useFaceCamera from "./useFaceCamera";
import useLiveness from "./useLiveness";

/* ---------------------------------- */
/* COMPONENT */
/* ---------------------------------- */

export default function AttendanceCamera(props) {
  /* ---------------------------------- */
  /* STATE */
  /* ---------------------------------- */

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uiStatus, setUiStatus] = useState("Loading models...");
  const [leftNames, setLeftNames] = useState([]);
  const [rightNames, setRightNames] = useState([]);
  const [enableLiveness, setEnableLiveness] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  /* ---------------------------------- */
  /* REDUX */
  /* ---------------------------------- */

  const { userData } = useSelector((state) => state.authentication);
  const branch = userData?.branch || props.loginBranch;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  /* ---------------------------------- */
  /* HIGH-SPEED & ACCURATE CONSTANTS */
  /* ---------------------------------- */
  const PER_PERSON_COOLDOWN = 10000; // 10s individual student lockout (prevents duplicates)
  const MIN_FACE_SIZE = 95; // Lowered from 110 for faster pickup at further distances
  const CAMERA_WARMUP_TIME = 300;
  const FAILED_MATCH_COOLDOWN = 500; // Fast retry on non-match

  /* ---------------------------------- */
  /* REFS */
  /* ---------------------------------- */

  const animationRef = useRef(null);
  const canvasRef = useRef(null);
  const statusRef = useRef("");
  const recentRef = useRef(new Map());
  const cooldownMapRef = useRef(new Map()); // Strict per-user debounce map
  const descriptorBufferRef = useRef([]); // 2-frame smoothing buffer to eliminate vector noise
  const globalApiLockRef = useRef(false);
  const frameProcessingRef = useRef(false);
  const cameraWarmupRef = useRef(true);
  const mountedRef = useRef(true);

  /* ---------------------------------- */
  /* LIVENESS */
  /* ---------------------------------- */

  const livenessSettings = useMemo(
    () => ({
      throttleMs: 40,
      smoothingWindow: 2,
      livenessFrames: 1,
      strictThreshold: 0.38,
      hardRejectThreshold: 0.52,
      requiredStrictFrames: 1,
      secondBestGap: 0.05,
      cooldownMs: 2000,
      enableLiveness,
    }),
    [enableLiveness]
  );

  const { isLive, confirmMatch, livenessStatus, resetLiveness, progress } =
    useLiveness(livenessSettings);

  /* ---------------------------------- */
  /* CAMERA */
  /* ---------------------------------- */

  const {
    videoRef,
    cameraOn,
    startCamera,
    stopCamera,
    status,
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    getVideoElement,
  } = useFaceCamera(modelsLoaded);

  /* ---------------------------------- */
  /* STATUS */
  /* ---------------------------------- */

  const updateStatus = useCallback((text) => {
    if (statusRef.current !== text) {
      statusRef.current = text;
      setUiStatus(text);
    }
  }, []);

  /* ---------------------------------- */
  /* RESET */
  /* ---------------------------------- */

  const resetDetectionState = useCallback(() => {
    globalApiLockRef.current = false;
    frameProcessingRef.current = false;
    cooldownMapRef.current.clear();
    descriptorBufferRef.current = [];
    resetLiveness();
  }, [resetLiveness]);

  /* ---------------------------------- */
  /* LOAD MODELS */
  /* ---------------------------------- */

  useEffect(() => {
    mountedRef.current = true;
    const loadModels = async () => {
      try {
        const MODEL_URL = "/models";
        updateStatus("Loading detector...");
        await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);

        updateStatus("Loading landmarks...");
        await faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL);

        updateStatus("Loading recognition...");
        await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);

        if (!mountedRef.current) return;
        setModelsLoaded(true);
        updateStatus("Ready ✅");
      } catch (err) {
        console.error(err);
        updateStatus(`❌ ${err.message}`);
      }
    };
    loadModels();
    return () => {
      mountedRef.current = false;
    };
  }, [updateStatus]);

  /* ---------------------------------- */
  /* CAMERA RESET */
  /* ---------------------------------- */

  useEffect(() => {
    if (cameraOn) {
      resetDetectionState();
      cameraWarmupRef.current = true;
      updateStatus("📷 Initializing camera...");

      const timer = setTimeout(() => {
        cameraWarmupRef.current = false;
        updateStatus("🙂 Ready for detection");
      }, CAMERA_WARMUP_TIME);

      return () => clearTimeout(timer);
    }
  }, [cameraOn, resetDetectionState, updateStatus]);

  /* ---------------------------------- */
  /* QUALITY CHECKS */
  /* ---------------------------------- */

  const isFaceSharpEnough = (landmarks) => {
    if (!landmarks) return false;
    const leftEye = landmarks.getLeftEye();
    const rightEye = landmarks.getRightEye();

    if (!leftEye?.length || !rightEye?.length) return false;

    const distance = Math.sqrt(
      Math.pow(rightEye[0].x - leftEye[0].x, 2) +
        Math.pow(rightEye[0].y - leftEye[0].y, 2)
    );
    // Lowered threshold from 24 to 18 to allow faster pickup without false rejection
    return distance > 18;
  };

  const isFacingFront = (landmarks) => {
    try {
      const nose = landmarks.getNose();
      const jaw = landmarks.getJawOutline();
      if (!nose?.length || !jaw?.length) return false;

      const noseX = nose[3].x;
      const faceCenter = (jaw[0].x + jaw[16].x) / 2;
      return Math.abs(noseX - faceCenter) < 24;
    } catch {
      return false;
    }
  };

  /* ---------------------------------- */
  /* DRAW BOUNDING BOX */
  /* ---------------------------------- */

  const drawDetection = (detection, video) => {
    const canvas = canvasRef.current;
    if (!canvas || !video || !detection) return;

    const displaySize = { width: video.clientWidth, height: video.clientHeight };
    canvas.width = displaySize.width;
    canvas.height = displaySize.height;

    const resizedDetection = faceapi.resizeResults(detection, displaySize);
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    faceapi.draw.drawDetections(canvas, resizedDetection);
  };

  /* ---------------------------------- */
  /* AUDIO FEEDBACK */
  /* ---------------------------------- */

  const speakName = (name) => {
    window.speechSynthesis.cancel();
    const msg = new SpeechSynthesisUtterance(`${name} marked successfully`);
    window.speechSynthesis.speak(msg);
  };

  /* ---------------------------------- */
  /* DATA MANAGEMENT SUCCESS STORE */
  /* ---------------------------------- */

  const handleSuccess = useCallback(
    (name) => {
      const now = Date.now();
      const last = recentRef.current.get(name);
      if (last && now - last < 10000) return;

      recentRef.current.set(name, now);
      updateStatus(`✅ ${name} marked`);
      speakName(name);

      setLeftNames((prev) => [name, ...prev.filter((n) => n !== name)].slice(0, 8));
      setRightNames((prev) => [{ name, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 8));
    },
    [updateStatus]
  );

  /* ---------------------------------------------------- */
  /* ASYNCHRONOUS BACKGROUND SNAPSHOT UPLOAD              */
  /* ---------------------------------------------------- */
  const captureAndUploadSnapshotAsync = (videoElement, studentId, onComplete) => {
    setTimeout(async () => {
      try {
        if (!videoElement) {
          onComplete("MANUAL_ENTRY_FALLBACK");
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = 480;
        canvas.height = 360;
        const context = canvas.getContext("2d");
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.70));
        if (!blob) {
          onComplete("MANUAL_ENTRY_FALLBACK");
          return;
        }

        const snapshotFile = new File([blob], `attendance_${studentId}_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        const urlResponse = await axios.get(
          `${process.env.REACT_APP_COLLEGE_ERP_API}get-upload-url`,
          {
            params: {
              fileName: snapshotFile.name,
              folder: "attendance-captures",
              contentType: snapshotFile.type,
            },
          }
        );

        const { uploadUrl, fileKey } = urlResponse.data;
        await axios.put(uploadUrl, snapshotFile, {
          headers: { "Content-Type": snapshotFile.type },
        });

        onComplete(fileKey);
      } catch (uploadErr) {
        console.error("Background snapshot upload error:", uploadErr);
        onComplete("MANUAL_ENTRY_FALLBACK");
      }
    }, 0);
  };

  /* ---------------------------------- */
  /* HIGH-SPEED DETECTION LOOP          */
  /* ---------------------------------- */

  useEffect(() => {
    if (!cameraOn || !modelsLoaded || props.isRedirecting) return;

    let cancelled = false;

    const runDetection = async (video) => {
      if (frameProcessingRef.current || globalApiLockRef.current) return;

      try {
        frameProcessingRef.current = true;

        const detection = await faceapi
          .detectSingleFace(
            video,
            new faceapi.SsdMobilenetv1Options({
              minConfidence: isMobile ? 0.28 : 0.35,
            })
          )
          .withFaceLandmarks(true)
          .withFaceDescriptor();

        if (!detection) {
          descriptorBufferRef.current = [];
          updateStatus("📷 Show your face");
          return;
        }

        drawDetection(detection, video);

        if (cameraWarmupRef.current) {
          updateStatus("📷 Stabilizing camera...");
          return;
        }

        const box = detection.detection.box;
        if (box.width < MIN_FACE_SIZE || box.height < MIN_FACE_SIZE) {
          descriptorBufferRef.current = [];
          updateStatus("📷 Move closer");
          return;
        }

        if (!isFaceSharpEnough(detection.landmarks)) {
          descriptorBufferRef.current = [];
          updateStatus("📷 Hold steady");
          return;
        }

        if (!isFacingFront(detection.landmarks)) {
          descriptorBufferRef.current = [];
          updateStatus("🙂 Look directly center");
          return;
        }

        if (enableLiveness) {
          const liveDetected = isLive(detection);
          if (!liveDetected || !confirmMatch()) return;
        }

        // Push to 2-frame descriptor buffer to cancel camera noise
        descriptorBufferRef.current.push(detection.descriptor);
        if (descriptorBufferRef.current.length > 2) {
          descriptorBufferRef.current.shift();
        }

        // Wait until we have 2 consecutive frames for smooth matching
        if (descriptorBufferRef.current.length < 2) {
          updateStatus("🙂 Hold steady...");
          return;
        }

        // Average the 2 frame descriptors to clear sensor noise
        const averagedDescriptor = new Float32Array(128);
        for (let i = 0; i < 128; i++) {
          averagedDescriptor[i] =
            (descriptorBufferRef.current[0][i] + descriptorBufferRef.current[1][i]) / 2;
        }

        // Lock pipeline instantly for matching
        globalApiLockRef.current = true;
        setProcessing(true);
        updateStatus("🔍 Matching...");

        const endpoint = props.isLoginMode
          ? `${process.env.REACT_APP_COLLEGE_ERP_API}${branch}/match-login`
          : `${process.env.REACT_APP_COLLEGE_ERP_API}faces/${branch}/match`;

        const res = await axios.post(
          endpoint,
          {
            branch: props.isLoginMode ? props.loginBranch : branch,
            descriptor: Array.from(averagedDescriptor),
          },
          {
            headers: { Authorization: `Bearer ${userData?.token}` },
          }
        );

        if (props.isLoginMode) {
          if (props.onLoginSuccess && res.data) {
            props.onLoginSuccess(res.data);
          }
          return;
        }

        const targetUserData = res.data?.user || (res.data?.employeeId ? res.data : null);
        const matchedId = targetUserData?.employeeId || targetUserData?.id || res.data?.matchedId;
        const name = targetUserData?.name || "Unknown User";
        const isSuccessfulMatch = res.data?.success === true || !!matchedId;

        if (!isSuccessfulMatch || !matchedId) {
          descriptorBufferRef.current = [];
          updateStatus("❌ Face not recognized");
          await new Promise((resolve) => setTimeout(resolve, FAILED_MATCH_COOLDOWN));
          return;
        }

        // PER-USER DUPLICATE CHECK: Skip if person was marked recently
        const lastMarkedTime = cooldownMapRef.current.get(matchedId);
        const now = Date.now();
        if (lastMarkedTime && now - lastMarkedTime < PER_PERSON_COOLDOWN) {
          descriptorBufferRef.current = [];
          updateStatus(`⚠️ ${name} already marked`);
          await new Promise((resolve) => setTimeout(resolve, 800));
          return;
        }

        // Mark user in cooldown map immediately
        cooldownMapRef.current.set(matchedId, now);

        // Immediate UI feedback
        setShowSuccessOverlay(true);
        setTimeout(() => setShowSuccessOverlay(false), 1200);
        handleSuccess(name);

        // Async non-blocking snapshot capture & final API synchronization
        captureAndUploadSnapshotAsync(video, matchedId, async (generatedPhotoKey) => {
          try {
            await axios.post(
              `${process.env.REACT_APP_COLLEGE_ERP_API}mark-attendance-manually`,
              {
                branch: branch || props.loginBranch || "Rehoboth",
                studentId: matchedId,
                studentName: name,
                photoKey: generatedPhotoKey,
              },
              {
                headers: { Authorization: `Bearer ${userData?.token}` },
              }
            );
          } catch (e) {
            console.error("Attendance synchronization background error:", e);
          }
        });

        descriptorBufferRef.current = [];
        resetLiveness();
      } catch (err) {
        console.error(err);
        const backendErrorMessage = err.response?.data?.message || "❌ Recognition error";
        updateStatus("❌ Error");
        setApiError(backendErrorMessage);
        setTimeout(() => setApiError(""), 5000);
        descriptorBufferRef.current = [];
      } finally {
        frameProcessingRef.current = false;
        setProcessing(false);
        // Instant release to scan the next person in line immediately
        globalApiLockRef.current = false;
      }
    };

    const detectFrame = async () => {
      if (cancelled) return;
      const video = getVideoElement?.() || videoRef.current;

      if (video && video.readyState >= 2) {
        await runDetection(video);
      }
      animationRef.current = requestAnimationFrame(detectFrame);
    };

    detectFrame();

    return () => {
      cancelled = true;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [
    cameraOn,
    modelsLoaded,
    enableLiveness,
    branch,
    userData?.token,
    getVideoElement,
    videoRef,
    isLive,
    confirmMatch,
    resetLiveness,
    handleSuccess,
    updateStatus,
    isMobile,
    props.isLoginMode,
    props.loginBranch,
    props.isRedirecting,
    props.onLoginSuccess,
  ]);

  /* ---------------------------------- */
  /* UI Layout Render */
  /* ---------------------------------- */

  return (
    <div style={{ display: "flex", gap: 10, padding: 10 }}>
      <div style={{ width: "20%" }}>
        <h4>📋 Marked</h4>
        {leftNames.map((n, i) => (
          <div key={i}>{n}</div>
        ))}
      </div>

      <div style={{ width: "60%", textAlign: "center" }}>
        <h3>Face Attendance</h3>

        <AnimatePresence>
          {apiError && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              style={{
                backgroundColor: "#ef4444",
                color: "#ffffff",
                padding: "12px 18px",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "24px",
                marginBottom: "15px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{apiError}</span>
              <button
                onClick={() => setApiError("")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ffffff",
                  cursor: "pointer",
                  fontSize: "16px",
                  marginLeft: "12px",
                }}
              >
                ✕
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {!cameraOn && (
          <button onClick={startCamera} disabled={!modelsLoaded}>
            Start
          </button>
        )}

        {cameraOn && (
          <>
            <select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
            >
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || "Camera"}
                </option>
              ))}
            </select>

            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: 500,
                margin: "0 auto",
                overflow: "hidden",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{ width: "100%", borderRadius: 10 }}
              />

              <canvas
                ref={canvasRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                }}
              />

              <AnimatePresence>
                {showSuccessOverlay && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.3 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    style={{
                      position: "absolute",
                      top: "25%",
                      left: "25%",
                      width: "50%",
                      height: "50%",
                      backgroundColor: "rgba(34, 197, 94, 0.9)",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.3)",
                      pointerEvents: "none",
                      zIndex: 10,
                    }}
                  >
                    <svg
                      width="50%"
                      height="50%"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button onClick={stopCamera}>Stop</button>
          </>
        )}

        <div style={{ marginTop: 10 }}>
          <label>
            <input
              type="checkbox"
              checked={enableLiveness}
              disabled={processing}
              onChange={(e) => setEnableLiveness(e.target.checked)}
            />{" "}
            {enableLiveness ? "Liveness Enabled" : "Liveness Disabled"}
          </label>
        </div>

        <p>{status}</p>
        <h4>{uiStatus}</h4>

        <motion.p key={livenessStatus}>{livenessStatus}</motion.p>

        <div
          style={{
            height: 8,
            background: "#eee",
            borderRadius: 10,
            overflow: "hidden",
            marginTop: 10,
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: progress > 80 ? "green" : "#007bff",
              transition: "0.3s",
            }}
          />
        </div>
      </div>

      <div style={{ width: "20%" }}>
        <h4>🔴 Live</h4>
        {rightNames.map((x, i) => (
          <div key={i}>
            {x.name} <small>{x.time}</small>
          </div>
        ))}
      </div>
    </div>
  );
}